import React, {useEffect, useState} from 'react'
import {alpha, Box, CircularProgress, Typography, useMediaQuery, useTheme} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import {useHistory} from 'react-router-dom'
import ReactHtmlParser from 'react-html-parser'
import {useAppSelector} from 'src/hooks'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import AppDialog from 'src/components-new/feedback/AppDialog'
import {CARD_BORDER_RADIUS, NEUTRAL_SCALE} from 'src/theme-new/tokens'
import PerformanceDetailDialog from 'src/pages-new/homepage/dialogs/PerformanceDetailDialog'
import TherapyReminderDialog from 'src/pages-new/homepage/dialogs/TherapyReminderDialog'
import ContactHelpDialog from 'src/pages-new/homepage/dialogs/ContactHelpDialog'
import CategoryPreviewDialog from 'src/pages-new/homepage/dialogs/CategoryPreviewDialog'
import CategoryTile from 'src/pages-new/homepage/CategoryTile'
import {CARD_META, CategoryRows, SUPPORTED_CTS, SupportedCt} from 'src/pages-new/homepage/cardRegistry'
import {PerformanceItem, DocumentItem, SecondaryTherapyItem, AppointmentItem, InfoMaterialItem} from 'src/pages-new/homepage/types'
import {mergeInfoMaterialsItems, mergeMyDocumentsItems} from 'src/pages-new/homepage/utils'
import {MaterialRecord, LinkRecord} from 'src/pages-new/infoMaterials/columns'

// --- normalizzazione contentType ---
const CT_ALIASES: Record<string, string> = {
  documenti: 'documents',
  terapie: 'therapies',
  appuntamenti: 'appointments',
  prestazioni: 'performances',
  'attività': 'activities',
  infomaterials: 'infoMaterials',
  materiali: 'infoMaterials',
  'info e materiali': 'infoMaterials',
  'materiali informativi': 'infoMaterials',
  pazienti: 'patients',
  diario: 'therapyDiary',
  diary: 'therapyDiary',
  therapydiary: 'therapyDiary',
  'richieste eco': 'echocardiographicRequests',
  echocardiographicrequests: 'echocardiographicRequests',
  'campioni biologici': 'biologicalSamples',
  biologicalsamples: 'biologicalSamples',
  exports: 'export',
  'richieste materiali': 'materialRequests',
  materialrequests: 'materialRequests',
  questionari: 'survey',
  surveys: 'survey',
  'i miei documenti': 'myDocuments',
  mydocuments: 'myDocuments',
}

function normalizeContentType(raw: string): string {
  const lower = raw.toLowerCase().trim()
  return CT_ALIASES[lower] ?? lower
}

const MAX_CARD_ITEMS = 4

// Card "elenco" che mostrano le prime voci della pagina corrispondente, senza azioni di dettaglio
type ListCt = 'patients' | 'therapyDiary' | 'echocardiographicRequests' | 'biologicalSamples' | 'export' | 'materialRequests' | 'survey'
  | 'myDocuments'

// 'myDocuments' è escluso: unisce due sorgenti in base alle tab attive, vedi useEffect
const LIST_FETCHERS: Record<Exclude<ListCt, 'myDocuments'>, (setStatus: (s: string) => void) => Promise<any>> = {
  patients: setStatus => apiClient.postPatientsSearch([], setStatus, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: []}),
  therapyDiary: setStatus => apiClient.getPatientDiary({page: 0, rowsPerPage: MAX_CARD_ITEMS, therapyType: 'PRIMARY'}, setStatus),
  echocardiographicRequests: setStatus => apiClient.getExamsRequests([], setStatus, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: '-createdAt'}),
  biologicalSamples: setStatus => apiClient.getTestDbs([], setStatus, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: []}),
  export: setStatus => apiClient.getExports([], setStatus, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: '-createdAt'}),
  materialRequests: setStatus => apiClient.getMaterialRequests({page: 0, size: MAX_CARD_ITEMS, sort: '-requestDate'}, setStatus),
  survey: setStatus => apiClient.getSurveys([], setStatus, {page: 0, rowsPerPage: MAX_CARD_ITEMS}),
}

const LIST_ERROR_LABELS: Record<ListCt, string> = {
  patients: 'i pazienti',
  therapyDiary: 'il diario',
  echocardiographicRequests: 'le richieste ECO',
  biologicalSamples: 'i campioni biologici',
  export: 'gli export',
  materialRequests: 'le richieste materiali',
  survey: 'i questionari',
  myDocuments: 'i documenti',
}

const LIST_CTS = Object.keys(LIST_ERROR_LABELS) as ListCt[]

const CONTACT_TEAL = '#2BA68C'
const CONTACT_TITLE_MOBILE = '#065f46'
const CONTACT_EMAIL = '#007DA4'

function ContentHtmlBlock({html, primaryColor}: {html: string; primaryColor: string}) {
  return (
    <Box
      sx={{
        color: NEUTRAL_SCALE[900],
        fontSize: 14,
        lineHeight: 1.6,
        '& *': {
          fontFamily: 'inherit !important',
          color: 'inherit',
          backgroundColor: 'transparent !important',
          lineHeight: 'inherit !important',
          maxWidth: '100%',
        },
        '& p': {margin: '0 0 12px'},
        '& p:last-child': {marginBottom: 0},
        // fontSize senza !important: i titoli senza dimensione esplicita restano al default, quella del configuratore prevale
        '& h1, & h2, & h3, & h4': {fontWeight: 700, fontSize: 'inherit', margin: '12px 0 8px'},
        '& ul, & ol': {paddingLeft: '20px', margin: '8px 0'},
        '& a': {color: primaryColor, textDecoration: 'underline'},
        '& img': {maxWidth: '100%', height: 'auto'},
      }}
    >
      {ReactHtmlParser(html)}
    </Box>
  )
}

export default function HomepageNew(): JSX.Element {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const history = useHistory()
  const userInfo = useAppSelector(state => state.userReducer.data)
  const homepageConfigurations = useAppSelector(state => state.pspConfigReducer.data.homepageConfigurations)
  const programEmail = useAppSelector(state => state.pspConfigReducer.data.programEmail)
  const sidebarItems = useAppSelector(state => state.pspConfigReducer.data.sidebarItems)
  const diaryType = useAppSelector(state => state.pspConfigReducer.data.therapyDiaryConfigurations?.diaryType)

  const [performances, setPerformances] = useState<PerformanceItem[]>([])
  const [documents, setDocuments] = useState<DocumentItem[]>([])
  const [secondaryTherapies, setSecondaryTherapies] = useState<SecondaryTherapyItem[]>([])
  const [appointments, setAppointments] = useState<AppointmentItem[]>([])
  const [activities, setActivities] = useState<any[]>([])
  const [infoMaterials, setInfoMaterials] = useState<MaterialRecord[]>([])
  const [infoLinks, setInfoLinks] = useState<LinkRecord[]>([])
  const [listItems, setListItems] = useState<Partial<Record<ListCt, any[]>>>({})

  const [statusPerformances, setStatusPerformances] = useState<string | undefined>()
  const [statusDocuments, setStatusDocuments] = useState<string | undefined>()
  const [statusTherapies, setStatusTherapies] = useState<string | undefined>()
  const [statusAppointments, setStatusAppointments] = useState<string | undefined>()
  const [statusActivities, setStatusActivities] = useState<string | undefined>()
  const [statusInfoMaterials, setStatusInfoMaterials] = useState<string | undefined>()
  const [statusInfoLinks, setStatusInfoLinks] = useState<string | undefined>()
  const [statusDownload, setStatusDownload] = useState<string | undefined>()
  const [listStatuses, setListStatuses] = useState<Partial<Record<ListCt, string>>>({})

  // Dialog dettaglio (desktop)
  const [selectedPerformance, setSelectedPerformance] = useState<PerformanceItem | null>(null)
  const [selectedTherapy, setSelectedTherapy] = useState<SecondaryTherapyItem | null>(null)
  const [contactDialogOpen, setContactDialogOpen] = useState(false)
  const [contentDialogOpen, setContentDialogOpen] = useState(false)
  // Modale categoria (mobile)
  const [openCard, setOpenCard] = useState<SupportedCt | null>(null)

  const firstName = userInfo?.firstName ?? ''
  const {
    desktopImage,
    mobileImage,
    programName,
    greenNumber,
    greenNumberDescription,
    content,
    cards,
  } = homepageConfigurations ?? {}

  // --- card visibili e normalizzate ---
  const SUPPORTED_TYPES = new Set<string>(SUPPORTED_CTS)
  const visibleCards = (cards ?? []).filter(c => c.visible && SUPPORTED_TYPES.has(normalizeContentType(c.contentType)))

  useEffect(() => {
    const neededTypes = new Set(visibleCards.map(c => normalizeContentType(c.contentType)))
    const today = new Date()
    const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
    const in5Years = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate())
    if (neededTypes.has('performances')) {
      apiClient.getUserPerformances(setStatusPerformances, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: ''})
        .then((data: any) => setPerformances(data ?? []))
        .catch(() => {})
    }
    if (neededTypes.has('documents')) {
      apiClient.getDocuments([], setStatusDocuments, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: '-createdAt'})
        .then((data: any) => setDocuments(data ?? []))
        .catch(() => {})
    }
    if (neededTypes.has('therapies')) {
      apiClient.getSecondaryTherapies(setStatusTherapies)
        .then((data: any) => setSecondaryTherapies((data ?? []).slice(0, MAX_CARD_ITEMS)))
        .catch(() => {})
    }
    if (neededTypes.has('appointments')) {
      apiClient.getUserEvents(today, in5Years, setStatusAppointments)
        .then((data: any) => setAppointments(
          (data ?? [])
            .filter((a: AppointmentItem) => a.startDate && new Date(a.startDate).getTime() >= today.getTime())
            .sort((a: AppointmentItem, b: AppointmentItem) =>
              new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime())
            .slice(0, MAX_CARD_ITEMS),
        ))
        .catch(() => {})
    }
    if (neededTypes.has('activities')) {
      apiClient.getPatientsActivities([], today, in30Days, setStatusActivities)
        .then((data: any) => setActivities((data ?? []).slice(0, MAX_CARD_ITEMS)))
        .catch(() => {})
    }
    if (neededTypes.has('infoMaterials')) {
      apiClient.getInfoMaterials([], setStatusInfoMaterials, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: '-createdAt'})
        .then((data: any) => setInfoMaterials(data ?? []))
        .catch(() => {})
      apiClient.getLinks([], setStatusInfoLinks, {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: '-createdAt'})
        .then((data: any) => setInfoLinks(data ?? []))
        .catch(() => {})
    }
    if (neededTypes.has('myDocuments')) {
      // Stesse tab di MyDocumentsNew: nascoste solo con `show: false` esplicito
      const showShared = sidebarItems?.sharedDocuments?.show !== false
      const showPersonal = sidebarItems?.personalDocuments?.show !== false
      const statuses: string[] = []
      const track = (i: number) => (s: string) => { statuses[i] = s }
      setListStatuses(prev => ({...prev, myDocuments: STATUS.PENDING}))
      const pagination = {page: 0, rowsPerPage: MAX_CARD_ITEMS, order: '-createdAt'}
      Promise.all([
        showPersonal ? apiClient.getDocuments([], track(0), pagination) : Promise.resolve([]),
        showShared ? apiClient.getInfoMaterials([], track(1), pagination) : Promise.resolve([]),
      ])
        .then(([personal, shared]: any[]) => {
          setListItems(prev => ({
            ...prev,
            myDocuments: mergeMyDocumentsItems(personal ?? [], shared ?? [], MAX_CARD_ITEMS),
          }))
          setListStatuses(prev => ({
            ...prev,
            myDocuments: statuses.includes(STATUS.REJECTED) ? STATUS.REJECTED : STATUS.FULFILLED,
          }))
        })
        .catch(() => setListStatuses(prev => ({...prev, myDocuments: STATUS.REJECTED})))
    }
    (Object.keys(LIST_FETCHERS) as Array<keyof typeof LIST_FETCHERS>).filter(ct => neededTypes.has(ct)).forEach(ct => {
      const setStatus = (s: string) => setListStatuses(prev => ({...prev, [ct]: s}))
      LIST_FETCHERS[ct](setStatus)
        .then((data: any) => setListItems(prev => ({...prev, [ct]: Array.isArray(data) ? data.slice(0, MAX_CARD_ITEMS) : []})))
        .catch(() => {})
    })
  }, [visibleCards.map(c => c.contentType).join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const downloadFile = async (doc: DocumentItem) => {
    if (!doc.fileId) return
    const response: any = await apiClient.download(doc.fileId, setStatusDownload)
    if (!response) return
    const blob = new Blob([new Uint8Array(response.data)], {type: response.headers['content-type']})
    const filepath = (window.URL || window.webkitURL).createObjectURL(blob)
    const link = document.createElement('a')
    link.href = filepath
    link.setAttribute('download', doc.fileName ?? 'documento')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadMaterial = (item: InfoMaterialItem) =>
    downloadFile({fileId: item.fileId, fileName: item.fileName ?? item.title})

  const saveBlob = (blob: Blob, fileName: string) => {
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Stesso comportamento di ExportsListNew: scaricabile solo se lo stato raw è 'created'
  const downloadExport = async (exp: any) => {
    if (!exp.fileServiceName) return
    const data = await apiClient.downloadExport(exp.fileServiceName, setStatusDownload)
    if (!data) return
    saveBlob(new Blob([data], {type: 'application/octet-stream'}), exp.filename ?? 'export')
  }

  // Stesso endpoint di EchocardiographicRequestsListNew; qui si scarica direttamente invece di aprire il viewer
  const downloadEchoReport = async (request: any) => {
    const report = await apiClient.downloadMedicalReport(request.patientId, request.examId, setStatusDownload)
    if (!report) return
    saveBlob(report.blob, report.isPdf ? 'referto.pdf' : report.fileName)
  }

  // Scheda infusionale: esiste solo per il diario della terapia primaria (DIARY_DRUGS), come in PrimaryAssumptionsPanel
  const downloadAssumption = async (assumption: any) => {
    const response: any = await apiClient.exportPatientAssumption(userInfo._id, assumption._id, setStatusDownload)
    if (!response) return
    const dateStr = assumption.date
      ? new Date(assumption.date).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '-')
      : ''
    saveBlob(new Blob([response.data], {type: 'application/pdf'}), `Scheda_infusionale_${dateStr}.pdf`)
  }
  const onDownloadAssumption = diaryType === 'DIARY_DRUGS' ? downloadAssumption : undefined

  const openLink = (item: InfoMaterialItem) => {
    if (item.url) window.open(item.url, '_blank', 'noopener')
  }

  const seeAllLink = (path: string) => (
    <Box
      onClick={() => history.push(path)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        color: '#1D4ED8',
        fontSize: 14,
        fontWeight: 500,
        '&:hover': {textDecoration: 'underline'},
      }}
    >
      Vedi tutti
      <ArrowForwardIcon sx={{fontSize: 16}} />
    </Box>
  )

  const firstRejectedMessage = (() => {
    if (statusPerformances === STATUS.REJECTED) return 'Errore nel recuperare le prestazioni.'
    if (statusDocuments === STATUS.REJECTED) return 'Errore nel recuperare i documenti.'
    if (statusTherapies === STATUS.REJECTED) return 'Errore nel recuperare le altre terapie.'
    if (statusAppointments === STATUS.REJECTED) return 'Errore nel recuperare gli appuntamenti.'
    if (statusActivities === STATUS.REJECTED) return 'Errore nel recuperare le attività.'
    if (statusInfoMaterials === STATUS.REJECTED || statusInfoLinks === STATUS.REJECTED) return 'Errore nel recuperare info e materiali.'
    const rejectedList = LIST_CTS.find(ct => listStatuses[ct] === STATUS.REJECTED)
    if (rejectedList) return `Errore nel recuperare ${LIST_ERROR_LABELS[rejectedList]}.`
    if (statusDownload === STATUS.REJECTED) return 'Errore nel scaricare il file.'
    return null
  })()

  const pickImage = (img: unknown): string | undefined =>
    (typeof img === 'string' && img.length > 0 ? img : undefined)
  const heroImage = isMobile
    ? (pickImage(mobileImage) ?? pickImage(desktopImage))
    : (pickImage(desktopImage) ?? pickImage(mobileImage))
  const heroImageSrc = heroImage
    ? (heroImage.startsWith('data:')
      ? heroImage
      : `data:image/${heroImage.startsWith('PHN2Zy') || heroImage.startsWith('PD94bWw') ? 'svg+xml' : 'png'};base64,${heroImage}`)
    : undefined

  // --- Costruzione descrittori carta ---
  const statusInfoMaterialsCard = statusInfoMaterials === STATUS.PENDING || statusInfoLinks === STATUS.PENDING
    ? STATUS.PENDING
    : statusInfoMaterials

  const itemsMap: Record<SupportedCt, any[]> = {
    performances,
    documents,
    therapies: secondaryTherapies,
    appointments,
    activities,
    infoMaterials: mergeInfoMaterialsItems(infoMaterials, infoLinks, MAX_CARD_ITEMS),
    patients: listItems.patients ?? [],
    therapyDiary: listItems.therapyDiary ?? [],
    echocardiographicRequests: listItems.echocardiographicRequests ?? [],
    biologicalSamples: listItems.biologicalSamples ?? [],
    export: listItems.export ?? [],
    materialRequests: listItems.materialRequests ?? [],
    survey: listItems.survey ?? [],
    myDocuments: listItems.myDocuments ?? [],
  }
  const statusMap: Record<SupportedCt, string | undefined> = {
    performances: statusPerformances,
    documents: statusDocuments,
    therapies: statusTherapies,
    appointments: statusAppointments,
    activities: statusActivities,
    infoMaterials: statusInfoMaterialsCard,
    patients: listStatuses.patients,
    therapyDiary: listStatuses.therapyDiary,
    echocardiographicRequests: listStatuses.echocardiographicRequests,
    biologicalSamples: listStatuses.biologicalSamples,
    export: listStatuses.export,
    materialRequests: listStatuses.materialRequests,
    survey: listStatuses.survey,
    myDocuments: listStatuses.myDocuments,
  }

  interface CardDescriptor {
    ct: SupportedCt
    title: string
  }

  const cardDescriptors: CardDescriptor[] = visibleCards.map(cardCfg => {
    const ct = normalizeContentType(cardCfg.contentType) as SupportedCt
    const meta = CARD_META[ct]
    return {
      ct,
      title: cardCfg.title?.trim() || meta.defaultTitle,
    }
  })

  // Dati della categoria aperta nella modale mobile
  const openMeta = openCard ? CARD_META[openCard] : null
  const openTitle = openCard ? (cardDescriptors.find(d => d.ct === openCard)?.title ?? openMeta?.defaultTitle ?? '') : ''
  const openStatus = openCard ? statusMap[openCard] : undefined
  const openItems = openCard ? itemsMap[openCard] : []

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      {/* A. H1 saluto — solo desktop */}
      {!isMobile && (
        <Typography variant='h1' sx={{mb: 0}}>{`Ciao ${firstName} 👋`}</Typography>
      )}

      {/* B. Hero card
           Mobile: card colorata con immagine piccola, saluto, nome programma e CTA — intera card cliccabile
           Desktop: immagine + testo affiancati */}
      {isMobile ? (
        <Card
          sx={{
            cursor: content ? 'pointer' : 'default',
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            borderColor: theme.palette.primary.main,
            borderRadius: CARD_BORDER_RADIUS,
          }}
        >
          <Box
            onClick={() => content && setContentDialogOpen(true)}
            sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, textAlign: 'center'}}
          >
            {heroImageSrc && (
              <Box
                component='img'
                src={heroImageSrc}
                alt=''
                sx={{width: 150, aspectRatio: '16 / 9', objectFit: 'contain'}}
              />
            )}
            <Box>
              <Typography sx={{fontWeight: 700, fontSize: 24, lineHeight: 1.3, color: NEUTRAL_SCALE[900]}}>
                {`Ciao ${firstName} 👋`}
              </Typography>
              {programName && (
                <Typography sx={{fontSize: 15, color: NEUTRAL_SCALE[900], mt: 0.5}}>
                  {`Benvenuto in ${programName}`}
                </Typography>
              )}
            </Box>
            <Typography sx={{fontWeight: 600, fontSize: 13, color: NEUTRAL_SCALE[900]}}>
              Scopri il tuo piano in pochi semplici passi
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <Box sx={{display: 'flex', flexDirection: 'row', gap: 3, alignItems: 'center'}}>
            {heroImageSrc ? (
              <Box
                component='img'
                src={heroImageSrc}
                alt=''
                sx={{flexShrink: 0, width: 300, aspectRatio: '16 / 9', borderRadius: 2, objectFit: 'contain'}}
              />
            ) : (
              <Box
                component='img'
                src='/assets/images/InsuranceOnlineDoctor.svg'
                alt=''
                sx={{flexShrink: 0, width: '40%', maxWidth: 360, maxHeight: 240, objectFit: 'contain', alignSelf: 'center'}}
              />
            )}
            {content ? (
              <Box sx={{flex: 1, minWidth: 0}}>
                <ContentHtmlBlock html={content} primaryColor={theme.palette.primary.main} />
              </Box>
            ) : (
              <Box sx={{flex: 1}}>
                <Typography variant='h2' sx={{mb: 1}}>
                  Benvenuto nel tuo portale,
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{mb: 2}}>
                  il tuo piano in pochi semplici passi:
                </Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                  {[
                    'Contatta lo sportello per informazioni sul tuo programma.',
                    'Consulta un case manager per il tuo percorso di cura.',
                    'Accedi al network di servizi dedicati a te.',
                  ].map((step, idx) => (
                    <Box key={idx} sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: '1px',
                        }}
                      >
                        <Typography sx={{fontSize: 11, fontWeight: 700, color: 'primary.main'}}>
                          {idx + 1}
                        </Typography>
                      </Box>
                      <Typography variant='body2' color='text.secondary'>
                        {step}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Card>
      )}

      {/* C. Griglia card — data-driven da homepageConfigurations.cards
           Mobile: griglia di tile-icona (3 colonne) → tap apre modale di categoria
           Desktop: 2 colonne con Card + ListItemRow e link "Vedi tutti" */}
      {cardDescriptors.length > 0 && (
        isMobile ? (
          <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3}}>
            {cardDescriptors.map(d => {
              const meta = CARD_META[d.ct]
              return (
                <CategoryTile
                  key={d.ct}
                  icon={<meta.Icon sx={{fontSize: 38}} />}
                  label={d.title}
                  onClick={() => setOpenCard(d.ct)}
                />
              )
            })}
          </Box>
        ) : (
          <Box sx={{display: 'grid', gridTemplateColumns: cardDescriptors.length === 1 ? '1fr' : 'repeat(2, 1fr)', gap: 3}}>
            {cardDescriptors.map(d => {
              const meta = CARD_META[d.ct]
              const status = statusMap[d.ct]
              const items = itemsMap[d.ct]
              return (
                <Card key={d.ct} title={d.title} action={seeAllLink(meta.path)}>
                  {status === STATUS.PENDING ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : items.length > 0 ? (
                    <CategoryRows
                      ct={d.ct}
                      items={items}
                      variant='desktop'
                      onDownloadDocument={downloadFile}
                      onSelectPerformance={setSelectedPerformance}
                      onSelectTherapy={setSelectedTherapy}
                      onDownloadMaterial={downloadMaterial}
                      onOpenLink={openLink}
                      onDownloadExport={downloadExport}
                      onDownloadEchoReport={downloadEchoReport}
                      onDownloadAssumption={onDownloadAssumption}
                    />
                  ) : (
                    <EmptyState
                      icon={meta.emptyIcon}
                      title={meta.emptyTitle}
                      description={meta.emptyDescription}
                    />
                  )}
                </Card>
              )
            })}
          </Box>
        )
      )}

      {/* D. Banner contatto
           Mobile: solo titolo su sfondo verde, click apre dialog
           Desktop: titolo + icona telefono + numero + descrizione */}
      {(greenNumber || programEmail) && (
        <Card
          sx={isMobile ? {
            backgroundColor: alpha(CONTACT_TEAL, 0.06),
            borderColor: alpha(CONTACT_TEAL, 0.25),
            cursor: 'pointer',
            '& .MuiCardContent-root': {pb: 2},
          } : undefined}
        >
          <Box
            onClick={isMobile ? () => setContactDialogOpen(true) : undefined}
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              alignItems: isMobile ? 'center' : 'flex-start',
              justifyContent: isMobile ? 'center' : undefined,
            }}
          >
            <Typography
              variant='h2'
              sx={{
                fontSize: isMobile ? 14 : 15,
                fontWeight: 700,
                color: isMobile ? CONTACT_TITLE_MOBILE : NEUTRAL_SCALE[900],
                mt: 0,
                mb: isMobile ? 0 : 1.5,
              }}
            >
              Hai bisogno di una mano?
            </Typography>
            {!isMobile && (
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                {/* Riga contatti: numero e email affiancati */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap'}}>
                  {greenNumber && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          backgroundColor: CONTACT_TEAL,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <LocalPhoneIcon sx={{color: '#fff', fontSize: 22}} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: CONTACT_TEAL,
                          lineHeight: 1.2,
                        }}
                      >
                        {greenNumber}
                      </Typography>
                    </Box>
                  )}
                  {programEmail && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          backgroundColor: CONTACT_EMAIL,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <EmailOutlinedIcon sx={{color: '#fff', fontSize: 22}} />
                      </Box>
                      <Box
                        component='a'
                        href={`mailto:${programEmail}`}
                        sx={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: CONTACT_EMAIL,
                          textDecoration: 'none',
                          wordBreak: 'break-word',
                          '&:hover': {textDecoration: 'underline'},
                        }}
                      >
                        {programEmail}
                      </Box>
                    </Box>
                  )}
                </Box>
                {/* Testo descrizione sotto */}
                {greenNumberDescription && (
                  <Box
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: NEUTRAL_SCALE[900],
                      '& *': {fontFamily: 'inherit !important', backgroundColor: 'transparent !important'},
                      '& p': {margin: 0},
                      '& a': {color: CONTACT_TEAL, textDecoration: 'underline'},
                      '& small, & .hours': {color: NEUTRAL_SCALE[500], fontSize: 12},
                    }}
                  >
                    {ReactHtmlParser(greenNumberDescription)}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Card>
      )}

      {/* E. InlineMessage errori */}
      {firstRejectedMessage && (
        <InlineMessage
          severity='error'
          onClose={() => {
            if (statusPerformances === STATUS.REJECTED) setStatusPerformances(undefined)
            if (statusDocuments === STATUS.REJECTED) setStatusDocuments(undefined)
            if (statusTherapies === STATUS.REJECTED) setStatusTherapies(undefined)
            if (statusAppointments === STATUS.REJECTED) setStatusAppointments(undefined)
            if (statusActivities === STATUS.REJECTED) setStatusActivities(undefined)
            if (statusInfoMaterials === STATUS.REJECTED) setStatusInfoMaterials(undefined)
            if (statusInfoLinks === STATUS.REJECTED) setStatusInfoLinks(undefined)
            if (statusDownload === STATUS.REJECTED) setStatusDownload(undefined)
            setListStatuses(prev => Object.fromEntries(
              Object.entries(prev).map(([ct, s]) => [ct, s === STATUS.REJECTED ? undefined : s]),
            ))
          }}
        >
          {firstRejectedMessage}
        </InlineMessage>
      )}

      {/* F. Dialogs */}

      {/* Desktop: dialog dettaglio per prestazioni e terapie */}
      <PerformanceDetailDialog
        open={selectedPerformance !== null}
        onClose={() => setSelectedPerformance(null)}
        performance={selectedPerformance}
      />
      <TherapyReminderDialog
        open={selectedTherapy !== null}
        onClose={() => setSelectedTherapy(null)}
        therapy={selectedTherapy}
      />

      {/* Contatto */}
      <ContactHelpDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        greenNumber={greenNumber}
        greenNumberDescription={greenNumberDescription}
        programEmail={programEmail}
      />

      {/* Dialog contenuto hero card (solo mobile) — centrato, non bottom-sheet */}
      <AppDialog
        open={contentDialogOpen}
        onClose={() => setContentDialogOpen(false)}
        title='Il tuo piano'
        maxWidth='xs'
        centeredOnMobile
      >
        {content && <ContentHtmlBlock html={content} primaryColor={theme.palette.primary.main} />}
      </AppDialog>

      {/* Mobile: modale di anteprima categoria */}
      {openCard && openMeta && (
        <CategoryPreviewDialog
          open={openCard !== null}
          onClose={() => setOpenCard(null)}
          title={openTitle}
          path={openMeta.path}
        >
          {openStatus === STATUS.PENDING ? (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
              <CircularProgress size={28} />
            </Box>
          ) : openItems.length > 0 ? (
            <CategoryRows
              ct={openCard}
              items={openItems}
              variant='modal'
              onDownloadDocument={downloadFile}
              onDownloadMaterial={downloadMaterial}
              onOpenLink={openLink}
              onDownloadExport={downloadExport}
              onDownloadEchoReport={downloadEchoReport}
              onDownloadAssumption={onDownloadAssumption}
            />
          ) : (
            <EmptyState
              icon={openMeta.emptyIcon}
              title={openMeta.emptyTitle}
              description={openMeta.emptyDescription}
            />
          )}
        </CategoryPreviewDialog>
      )}
    </Box>
  )
}
